import { number } from "framer-motion";

/**
 * Format phone number for display
 * e.g., "+14155551234" -> "+1 (415) 555-1234"
 */
export function formatPhoneNumber(phone: string): string {
  if (!phone) return '';

  // Remove all non-digit chars except leading +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // US format
  if (cleaned.startsWith('+1') && cleaned.length === 12) {
    const area = cleaned.slice(2, 5);
    const first = cleaned.slice(5, 8);
    const last = cleaned.slice(8);
    return `+1 (${area}) ${first}-${last}`;
  }

  // Indian format
  if (cleaned.startsWith('+91') && cleaned.length === 13) {
    const part1 = cleaned.slice(3, 8);
    const part2 = cleaned.slice(8);
    return `+91 ${part1} ${part2}`;
  }

  return cleaned;
}

/**
 * Format a date for conversation list:
 * - Today: "2:30 PM"
 * - Yesterday: "Yesterday"
 * - Older: "Jan 15" or "Jan 15, 2024" (different year)
 */
export function formatRelativeTime(date: Date | string | number): string {
  const now = new Date();
  const d = new Date(date);

  const isToday =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();

  if (isToday) {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    d.getDate() === yesterday.getDate() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getFullYear() === yesterday.getFullYear();

  if (isYesterday) return 'Yesterday';

  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Format a date as time only for chat message bubbles.
 * `'12h'` (default) → "3:45 PM"; `'24h'` → "15:45" (Settings › Chat View ›
 * Time Format, `preferences.chatTimeFormat`).
 */
export function formatMessageTime(date: Date | string | number, timeFormat: '12h' | '24h' = '12h'): string {
  const d = new Date(date);
  if (timeFormat === '24h') {
    // hourCycle 'h23' (not hour12:false) — Chrome renders midnight as "24:00" with hour12:false.
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  }
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

/**
 * Format UC credits for display
 */
export function formatCredits(credits: number): string {
  let restrictDecimals = credits % 1 === 0;
  if (credits >= 1000000) return restrictDecimals ? `${(credits / 1000000)}M` : `${(credits / 1000000).toFixed(1)}M`;
  if (credits >= 1000) return restrictDecimals ? `${(credits / 1000)}K` : `${(credits / 1000).toFixed(1)}K`;
  return credits.toString();
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Format a timestamp as relative time ago string.
 * e.g., "2 minutes ago", "3 hours ago", "4 days ago"
 */
export function formatTimeAgo(date: Date | string | number): string {
  const now = Date.now();
  const d = new Date(date).getTime();
  const diffMs = now - d;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? 's' : ''} ago`;
  if (diffDay < 30) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Clean a sender label by removing redundant phone number suffixes.
 * e.g., "(734) 720-8080 (+17347208080)" → "(734) 720-8080"
 */
export function cleanSenderLabel(label: string): string {
  if (!label) return '';
  // Wrap in String() — some TPAs pass numeric sender IDs (e.g. 917834811114)
  const str = String(label);
  return str.replace(/\s*\(\+\d+\)\s*$/, '').trim();
}

/**
 * Format a date for display
 */
export function formatDate(date: Date | string | number, options?: Intl.DateTimeFormatOptions): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options,
  });
}

/**
 * Obfuscate a conversation id for use in `window.location.hash`.
 *
 * Conversation ids vary by TPA:
 *   - SMS / WhatsApp phone-pair ids, e.g.
 *       `+14155550100::+447825552154`          (Twilio SMS, phone-only TPAs)
 *       `whatsapp:+1…::whatsapp:+4…`           (Twilio WhatsApp)
 *   - Opaque provider ids, e.g. Twilio conversation SIDs, Intercom ids,
 *     email thread ids, etc.
 *
 * Putting any of these raw in the URL can leak contact identifiers into
 * browser history / analytics / referrers, so both branches are encoded
 * before writing to the hash. Encoding is deterministic so shareable URLs
 * still work.
 *
 * Strategy:
 *   - Phone-pair shape — strip `+`, base36-encode each number, join with
 *     `.`, prefix with a 2-char channel flag (`ww`/`ws`/`sw`/`ss`). This
 *     is the common case for Twilio / WhatsApp / other phone TPAs and
 *     shrinks a 27-char id to ~18 chars (beats base64's 36).
 *   - Everything else — URL-safe base64 with a leading `b` prefix so the
 *     decoder knows which branch to take. Handles non-ASCII via the
 *     `unescape(encodeURIComponent(...))` wrapper.
 */
/**
 * Per-side flag chars for the phone-pair branch. Each char encodes two bits:
 *   - uppercase = `+` prefix on the number, lowercase = no `+`
 *   - `W`/`w`   = `whatsapp:` channel prefix
 *   - `S`/`s`   = no channel prefix (plain SMS / phone-only TPAs)
 * Example: `Ws6igyrlc.6dh9fy4c` = `whatsapp:+<num>::<num>` (no + on right).
 */
function flagFor(hasWa: boolean, hasPlus: boolean): string {
  const base = hasWa ? 'w' : 's';
  return hasPlus ? base.toUpperCase() : base;
}
function parseFlag(ch: string): { wa: boolean; plus: boolean } | null {
  const lower = ch.toLowerCase();
  if (lower !== 'w' && lower !== 's') return null;
  return { wa: lower === 'w', plus: ch !== lower };
}

export function encodeConvIdForHash(id: string): string {
  if (!id) return '';
  // Note: `+` is optional on each side. WhatsApp TPAs frequently drop it on
  // one or both legs (`whatsapp:14155550100::whatsapp:447825552154`). Each
  // side's `+`-presence is preserved through the flag char so we round-trip
  // the exact original string.
  const m = /^(whatsapp:)?(\+?)(\d{1,16})::(whatsapp:)?(\+?)(\d{1,16})$/.exec(id);
  if (m) {
    const [, waA, plusA, numA, waB, plusB, numB] = m;
    const a = Number(numA);
    const b = Number(numB);
    if (Number.isSafeInteger(a) && Number.isSafeInteger(b)) {
      const flag = flagFor(!!waA, !!plusA) + flagFor(!!waB, !!plusB);
      return `${flag}${a.toString(36)}.${b.toString(36)}`;
    }
  }
  // Fallback: base64url for anything non-phone-pair (Twilio SIDs, Intercom
  // ids, email thread ids, …). Leading `b` lets the decoder pick the branch.
  try {
    return 'b' + btoa(unescape(encodeURIComponent(id)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  } catch {
    return '';
  }
}

/** Inverse of {@link encodeConvIdForHash}. Returns `null` on malformed input. */
export function decodeConvIdFromHash(token: string): string | null {
  if (!token) return null;
  // base64url fallback branch
  if (token.startsWith('b')) {
    try {
      const b64 = token.slice(1).replace(/-/g, '+').replace(/_/g, '/');
      const pad = b64 + '==='.slice((b64.length + 3) % 4);
      return decodeURIComponent(escape(atob(pad)));
    } catch {
      return null;
    }
  }
  // Compact phone-pair branch: `<flagA><flagB><base36>.<base36>`
  const m = /^([WwSs])([WwSs])([0-9a-z]+)\.([0-9a-z]+)$/.exec(token);
  if (!m) return null;
  const [, fA, fB, a36, b36] = m;
  const flagA = parseFlag(fA);
  const flagB = parseFlag(fB);
  if (!flagA || !flagB) return null;
  const a = parseInt(a36, 36);
  const b = parseInt(b36, 36);
  if (!Number.isSafeInteger(a) || !Number.isSafeInteger(b)) return null;
  const buildLeg = (flag: { wa: boolean; plus: boolean }, n: number) =>
    (flag.wa ? 'whatsapp:' : '') + (flag.plus ? '+' : '') + n;
  return `${buildLeg(flagA, a)}::${buildLeg(flagB, b)}`;
}
